<template>
    <div>
        <div class="mb-5 rounded-sm bg-green-300 p-6">
            <p class="mb-4 leading-none">
                <strong>{{ orderDateLabel }}:</strong> {{ formatDate(order.orderDate ?? order.orderDateTime) }}
            </p>
            <p class="mb-4 leading-none" v-if="order.deliveries?.[0]?.shippingOrderAddress">
                <strong>Bezorgadres:</strong>
                {{ order.deliveries[0].shippingOrderAddress.firstName }}
                {{ order.deliveries[0].shippingOrderAddress.lastName }}
            </p>
            <p class="mb-4 leading-none" v-if="!isTritonOrder"><strong>Ordernummer:</strong> {{ order.orderNumber }}</p>
            <p class="mb-4 leading-none"><strong>Aantal items:</strong> {{ order.lineItems.length }}</p>
            <p class="mb-4 leading-none" v-if="!isTritonOrder"><strong>Status:</strong> {{ orderStatusLabel }}</p>
            <p class="leading-none" v-else>
                <strong>{{ orderStatusLabel }}</strong>
            </p>
            <p class="leading-none" v-if="!isTritonOrder">
                <strong>Betaalwijze:</strong>
                {{
                    order.transactions[order.transactions.length - 1]?.paymentMethod?.translated?.name ||
                    snippets['headless.order.paymentMethodUnknown']
                }}
            </p>
        </div>

        <!-- Item rows follow the sideCart/Item.vue look: 70x95 image, xsm typography, chips, bold price -->
        <div class="py-3">
            <template v-for="item in order.lineItems" :key="item.id">
                <div
                    class="mb-[10px] flex items-center gap-4 border-b border-green-500 pb-4 last:border-none last:pb-0 lg:gap-6">
                    <div class="h-[130px] w-[96px] shrink-0">
                        <ShopwareImg
                            v-if="item.type === 'product'"
                            class="h-full w-full rounded-sm object-cover"
                            :alt="item?.cover?.alt"
                            :src="item?.cover?.url"
                            :width="96" />

                        <div
                            v-else-if="item.type === 'promotion'"
                            class="flex h-full w-full flex-col items-center justify-center bg-green-200 text-green-700">
                            <ReceiptPercentIcon class="size-12" />
                        </div>
                    </div>
                    <div class="flex w-full flex-col justify-between">
                        <div>
                            <h3
                                class="font-mori text-xsm mb-2 leading-none font-semibold"
                                v-if="item.payload?.manufacturerName">
                                {{ item.payload.manufacturerName }}
                            </h3>
                            <p class="text-xsm mb-2 leading-none">
                                {{ item.label }}
                            </p>
                        </div>
                        <div class="flex flex-wrap items-center justify-between gap-1">
                            <div>
                                <span class="text-xsm mr-1 bg-green-200 p-[2px]">{{ item.quantity }}x</span>
                                <span
                                    v-for="option in itemOptions(item)"
                                    :key="option"
                                    class="text-xsm mr-1 bg-green-200 p-[2px]">
                                    {{ option }}
                                </span>
                            </div>
                            <p class="text-sm font-bold text-black"><Price :price="item.totalPrice" /></p>
                        </div>
                    </div>
                </div>
            </template>
        </div>
        <div class="mt-2 mb-6 rounded-sm bg-green-300 p-6">
            <p class="leading-none">
                <strong>Totaal bedrag:</strong> <Price :price="order.price.totalPrice" />
            </p>
        </div>
        <!-- TritonX transactions carry no points data, so the (webshop) saving estimate would always show 0 -->
        <ShopwareProductLoyaltySavingUsp
            v-if="!isTritonOrder && loyaltyBase > 0"
            class="mb-6!"
            :list-price="loyaltyBase">
            <template #text>
                {{ snippets['headless.account.loyaltyUspOrder'] ?? 'Je spaarbonus voor deze bestelling is ' }}
            </template>
        </ShopwareProductLoyaltySavingUsp>
        <button class="btn btn-primary !w-full" v-if="hasDocuments" @click="downloadInvoice">Download factuur</button>
        <button
            class="btn btn-primary mt-4 !w-full"
            v-if="trackingUrls && trackingUrls?.length > 0"
            v-for="trackingUrl in trackingUrls"
            @click="goToTrackAndTrace(trackingUrl)">
            Track & Trace
        </button>
    </div>
</template>

<script lang="ts" setup>
    import { computed } from 'vue';
    import { CheckCircleIcon, ReceiptPercentIcon } from '@heroicons/vue/24/solid';

    const props = defineProps({ orderDetails: Object });
    const { getCountries, fetchCountries } = useCountries();

    // TritonX in-store orders are synthetic: they only exist in the merged order listing and
    // cannot be fetched by id. For those we render the object we already received from the list;
    // real webshop orders keep the detail fetch (documents, tracking, payment method, etc.).
    const isTritonOrder = computed(() => !!props.orderDetails?.customFields?.tritonx_order);

    const { order: fetchedOrder, documents, hasDocuments, loadOrderDetails } = useOrderDetails(props.orderDetails?.id);

    const snippetsStore = useSnippetsStore();
    const snippets = snippetsStore.snippets;

    await fetchCountries();

    if (!isTritonOrder.value) {
        await loadOrderDetails();
    }

    const order = computed(() => (isTritonOrder.value ? props.orderDetails : fetchedOrder.value));

    // In-store purchases use receipt wording instead of webshop wording
    const orderDateLabel = computed(() =>
        isTritonOrder.value
            ? snippets?.['headless.account.purchasedOn'] || 'Gekocht op'
            : snippets?.['headless.account.orderedOn'] || 'Besteld op',
    );

    const orderStatusLabel = computed(() => {
        if (isTritonOrder.value) {
            return snippets?.['headless.account.inStorePurchase'] || 'In winkel gekocht';
        }

        return order.value?.stateMachineState?.translated?.name ?? '';
    });

    const trackingUrls = computed(() => {
        const delivery = order.value?.deliveries?.[0];
        const selectedCountryId = delivery?.shippingOrderAddress?.countryId;
        const selectedCountry = getCountries.value.find(country => country.id === selectedCountryId);
        const iso = selectedCountry?.iso.toLowerCase() ?? '';
        const trackingCodes = delivery?.trackingCodes;
        const postalCode = delivery?.shippingOrderAddress?.zipcode?.replace(/\s/g, '');

        return trackingCodes?.map(trackingCode => {
            return `https://ballegooyenmodes.shipping-portal.com/tracking/?country=${iso}&tracking_number=${trackingCode}&postal_code=${postalCode}`;
        });
    });

    function goToTrackAndTrace(trackingUrl): void {
        navigateTo(trackingUrl, {
            external: true,
            open: {
                target: '_blank',
            },
        });
    }

    function downloadInvoice(): void {
        // Download invoice
    }

    // Option chips in the same style as the sidecart (which hides the color option). Webshop
    // items carry payload.options; TritonX in-store items only have a size in their custom fields.
    function itemOptions(item: any): string[] {
        const size = item.customFields?.tritonx_line_item?.size;
        if (size) {
            return [`Maat: ${size}`];
        }

        return (item.payload?.options ?? [])
            .filter((option: any) => option.group !== 'Kleur')
            .map((option: any) => `${option.group}: ${option.option}`);
    }

    function formatDate(dateString: string): string {
        const date = new Date(dateString);
        const day = date.getDate();
        const year = date.getFullYear();

        const monthFormatter = new Intl.DateTimeFormat('nl', { month: 'long' });
        const month = monthFormatter.format(date);

        return `${day} ${month} ${year}`;
    }

    const loyaltyBase = computed(() => getOrderNonSaleTotal(order.value?.lineItems));
</script>
